/**
 * Carrusel de días del listado operativo.
 * Fechas del mes calendario ART con al menos una factura (borrador o
 * confirmada). Incluye días > hoy del mismo mes si hay factura (venta
 * corrida por día cerrado). No fabrica chips futuros vacíos.
 * Cierre abierto vacío (0 facturas) no entra. El estilo de cierre es secundario.
 * No se fabrican Anteayer / Ayer / Hoy si caen en el mes anterior o no hay factura.
 */

const TZ = "America/Argentina/Buenos_Aires";

export const DEFAULT_FACTURA_PAGE_SIZE = 10;
export const FACTURA_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function todayYmd(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function addDaysYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(year, month - 1, day + days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function isSundayYmd(ymd: string): boolean {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() === 0;
}

export function ymdParts(ymd: string): { year: number; month: number; day: number } {
  const [year, month, day] = ymd.split("-").map(Number);
  return { year, month, day };
}

export function monthStartYmd(ymd: string): string {
  const { year, month } = ymdParts(ymd);
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

export function monthEndYmd(ymd: string): string {
  const { year, month } = ymdParts(ymd);
  const last = daysInCalendarMonth(year, month);
  return `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}

export function daysInCalendarMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function toFechaYmd(fecha: unknown): string {
  if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    return fecha.slice(0, 10);
  }
  return "";
}

export type CierreDiaChip = {
  fecha?: unknown;
  cerrado?: boolean;
  sucursalId?: number | null;
  sucursal_id?: number | null;
  tieneCierre?: boolean;
  tiene_cierre?: boolean;
  tieneFactura?: boolean;
  tiene_factura?: boolean;
};

export function sucursalIdOfCierreDia(cierre: CierreDiaChip): number | null {
  const raw = cierre.sucursalId ?? cierre.sucursal_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function normalizeCierreDiasPayload(payload: unknown): CierreDiaChip[] {
  if (Array.isArray(payload)) return payload as CierreDiaChip[];
  if (payload && typeof payload === "object") {
    const results = (payload as { results?: unknown }).results;
    if (Array.isArray(results)) return results as CierreDiaChip[];
  }
  return [];
}

function sucursalWanted(sucursalId?: number | null): number | null {
  if (sucursalId == null) return null;
  const n = Number(sucursalId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function cierreMatchesSucursal(cierre: CierreDiaChip, sucursalId?: number | null): boolean {
  const want = sucursalWanted(sucursalId);
  if (want == null) return true;
  const sid = sucursalIdOfCierreDia(cierre);
  if (sid == null) return true;
  return sid === want;
}

export function fixedCierreWindow(today: string = todayYmd()): {
  anteayer: string;
  ayer: string;
  hoy: string;
} {
  return {
    anteayer: addDaysYmd(today, -2),
    ayer: addDaysYmd(today, -1),
    hoy: today,
  };
}

export function cierreChipLabel(ymd: string, today: string = todayYmd()): string {
  const start = monthStartYmd(today);
  const w = fixedCierreWindow(today);
  if (ymd === w.hoy) return "Hoy";
  if (ymd === w.ayer && ymd >= start) return "Ayer";
  if (ymd === w.anteayer && ymd >= start) return "Anteayer";
  return String(ymdParts(ymd).day);
}

export const CIERRE_CHIP_W = 76;
export const CIERRE_CHIP_H = 32;
export const CIERRE_CHIP_GAP = 6;
export const CIERRE_CHIP_PEEK = 22;
export const CIERRE_CHIP_VISIBLE = 3;
export const CIERRE_CHIP_STEP = CIERRE_CHIP_W + CIERRE_CHIP_GAP;
/** Movement below this is a tap (select that chip), not a swipe. */
export const CIERRE_CHIP_CLICK_SLOP = 10;

export function clampNumber(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function carouselMaxIndex(chipCount: number): number {
  return Math.max(0, chipCount - CIERRE_CHIP_VISIBLE);
}

export function carouselShowChrome(chipCount: number): boolean {
  return chipCount > CIERRE_CHIP_VISIBLE;
}

/** Snap the viewport start index from a drag delta (iPhone-style paging). */
export function carouselSnapIndex(index: number, dx: number, maxIndex: number): number {
  return clampNumber(index - Math.round(dx / CIERRE_CHIP_STEP), 0, maxIndex);
}

/** Right-hand slot (Hoy at rest) — the cierre that lands on release. */
export function carouselAnchorIndex(snapI: number, chipCount: number): number {
  return clampNumber(snapI + CIERRE_CHIP_VISIBLE - 1, 0, Math.max(0, chipCount - 1));
}

export function carouselPeekFlags(
  index: number,
  maxIndex: number,
  showChrome: boolean,
): { peekLeft: boolean; peekRight: boolean } {
  return {
    peekLeft: showChrome && index > 0,
    peekRight: showChrome && index < maxIndex,
  };
}

export function carouselViewportWidth(
  peekLeft: boolean,
  peekRight: boolean,
  chipCount: number = CIERRE_CHIP_VISIBLE,
): number {
  const visible = Math.min(CIERRE_CHIP_VISIBLE, Math.max(0, chipCount));
  if (visible <= 0) return 0;
  return (
    visible * CIERRE_CHIP_W +
    (visible - 1) * CIERRE_CHIP_GAP +
    (peekLeft ? CIERRE_CHIP_PEEK : 0) +
    (peekRight ? CIERRE_CHIP_PEEK : 0)
  );
}

export function carouselTranslateX(index: number, peekLeft: boolean, drag: number): number {
  return -(index * CIERRE_CHIP_STEP) + (peekLeft ? CIERRE_CHIP_PEEK : 0) + drag;
}

export function carouselSlotLeft(peekLeft: boolean): number {
  return (peekLeft ? CIERRE_CHIP_PEEK : 0) + (CIERRE_CHIP_VISIBLE - 1) * CIERRE_CHIP_STEP;
}

export function carouselIsClick(dx: number): boolean {
  return Math.abs(dx) < CIERRE_CHIP_CLICK_SLOP;
}

/** Inclusive range of fully visible chips at this window start. */
export function carouselVisibleRange(
  index: number,
  chipCount: number,
): { start: number; end: number } {
  if (chipCount <= 0) return { start: 0, end: -1 };
  const start = clampNumber(index, 0, carouselMaxIndex(chipCount));
  const end = Math.min(chipCount - 1, start + CIERRE_CHIP_VISIBLE - 1);
  return { start, end };
}

/** Window start so `chipIndex` is fully on-screen (peeked chips scroll in). */
export function carouselIndexToReveal(
  chipIndex: number,
  windowIndex: number,
  chipCount: number,
): number {
  const maxI = carouselMaxIndex(chipCount);
  if (chipCount <= 0) return 0;
  const i = clampNumber(chipIndex, 0, chipCount - 1);
  const start = clampNumber(windowIndex, 0, maxI);
  const lastVisible = start + CIERRE_CHIP_VISIBLE - 1;
  if (i >= start && i <= lastVisible) return start;
  if (i < start) return clampNumber(i, 0, maxI);
  return clampNumber(i - CIERRE_CHIP_VISIBLE + 1, 0, maxI);
}

export function carouselChipIndexAtX(
  localX: number,
  index: number,
  peekLeft: boolean,
  drag: number,
  chipCount: number,
): number {
  if (chipCount <= 0) return 0;
  const trackX = localX - (peekLeft ? CIERRE_CHIP_PEEK : 0) - drag + index * CIERRE_CHIP_STEP;
  return clampNumber(Math.floor(trackX / CIERRE_CHIP_STEP), 0, chipCount - 1);
}

export type CarouselPointerRelease = {
  nextIndex: number;
  selectYmd: string | null;
};

/**
 * Tap (incl. peeked chip) → that day. Swipe → only move the window
 * (Hoy stays selectable on the right until the user clicks an older chip).
 */
export function carouselPointerRelease(opts: {
  days: string[];
  index: number;
  dx: number;
  downChipIndex: number;
}): CarouselPointerRelease {
  const { days, index, dx, downChipIndex } = opts;
  const maxI = carouselMaxIndex(days.length);
  if (carouselIsClick(dx)) {
    const chipI = clampNumber(downChipIndex, 0, Math.max(0, days.length - 1));
    return {
      nextIndex: carouselIndexToReveal(chipI, index, days.length),
      selectYmd: days[chipI] ?? null,
    };
  }
  return {
    nextIndex: carouselSnapIndex(index, dx, maxI),
    selectYmd: null,
  };
}

/** Chip de Ventas = hay factura. Cierre vacío no cuenta. */
export function chipTieneFactura(cierre: CierreDiaChip): boolean {
  if (cierre.tieneFactura === false || cierre.tiene_factura === false) return false;
  if (cierre.tieneFactura === true || cierre.tiene_factura === true) return true;
  // Legado F3: fila con tiene_cierre false era factura sin cierre.
  if (cierre.tieneCierre === false || cierre.tiene_cierre === false) return true;
  return false;
}

function cierreYmdsInCurrentMonth(
  cierres: CierreDiaChip[],
  today: string,
  sucursalId?: number | null,
): Set<string> {
  const start = monthStartYmd(today);
  const monthEnd = monthEndYmd(today);
  const withRow = new Set<string>();
  for (const cierre of cierres) {
    const ymd = toFechaYmd(cierre.fecha);
    if (!ymd) continue;
    if (!cierreMatchesSucursal(cierre, sucursalId)) continue;
    if (ymd < start || ymd > monthEnd) continue;
    if (!chipTieneFactura(cierre)) continue;
    withRow.add(ymd);
  }
  return withRow;
}

/**
 * Días del mes actual ART con factura, incluido un día futuro del mes
 * si la venta se corrió (día cerrado). No mes anterior, no huecos, no
 * cierre vacío, no futuros sin factura.
 */
export function carouselChipDays(
  cierres: CierreDiaChip[],
  today: string = todayYmd(),
  sucursalId?: number | null,
): string[] {
  const { year, month } = ymdParts(today);
  const last = daysInCalendarMonth(year, month);
  const withRow = cierreYmdsInCurrentMonth(cierres, today, sucursalId);
  const out: string[] = [];
  for (let day = 1; day <= last; day += 1) {
    const ymd = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (!withRow.has(ymd)) continue;
    out.push(ymd);
  }
  return out;
}

/**
 * Días del mes con factura, excluyendo Anteayer/Ayer/Hoy
 * cuando esas fechas caen en el mes actual.
 */
export function carouselOlderDays(
  cierres: CierreDiaChip[],
  today: string = todayYmd(),
  sucursalId?: number | null,
): string[] {
  const fixed = new Set(Object.values(fixedCierreWindow(today)));
  return carouselChipDays(cierres, today, sucursalId).filter((ymd) => !fixed.has(ymd));
}

function chipTieneCierre(cierre: CierreDiaChip): boolean {
  if (cierre.tieneCierre === false || cierre.tiene_cierre === false) return false;
  return true;
}

export function cierreEstadoOf(
  cierres: CierreDiaChip[],
  ymd: string,
  sucursalId?: number | null,
): "abierto" | "cerrado" | "sin" {
  for (const cierre of cierres) {
    if (toFechaYmd(cierre.fecha) !== ymd) continue;
    if (!cierreMatchesSucursal(cierre, sucursalId)) continue;
    if (!chipTieneCierre(cierre)) continue;
    return cierre.cerrado ? "cerrado" : "abierto";
  }
  return "sin";
}

export function previousMonthValue(today: string = todayYmd()): string {
  const prev = addDaysYmd(monthStartYmd(today), -1);
  const { year, month } = ymdParts(prev);
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function monthValueToFechaRange(
  monthValue: string,
): { fecha__gte: string; fecha__lte: string } | null {
  if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) return null;
  const [year, month] = monthValue.split("-").map(Number);
  const last = daysInCalendarMonth(year, month);
  const mm = String(month).padStart(2, "0");
  return {
    fecha__gte: `${year}-${mm}-01`,
    fecha__lte: `${year}-${mm}-${String(last).padStart(2, "0")}`,
  };
}
